import React, { memo, useEffect, useMemo } from 'react'
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next'
import { Container, useProductQuery } from '@thirstycamel/ui'
import { useRouter } from 'next/router'
import Delay from 'react-delay'
import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'

import LayoutMain from '../../../../layout/LayoutMain'
import { useStore } from '../../../../store/hooks'
import SpecialsSection from '../../../../components/SpecialsSection'
import Advert from '../../../../components/Advert'
import ProductReviews from '../../../../components/ProductReviews'
import ProductOverview from '../../../../components/ProductOverview'
import { parseCookies } from 'nookies'
import { QueryCache } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { backend } from '../../../../utils/backend'
import imageProxy from '../../../../utils/imageProxy'
import { eventGA4, eventUA } from '../../../../utils/gtag'
import { useGoogleAnalytics } from '../../../../components/GoogleAnalyticsProvider'

const USED_IMAGE_SIZES = [1200, 400, 100]

import geoip from 'geoip-lite'
import requestIp from 'request-ip'
import { ProductBundleOverview } from '../../../../components/ProductBundleOverview'
import { ProductType } from '@ts/core/src/modules/coreproduct/coreproduct.types'
import { track } from '../../../../hooks/useKlaviyo'

const tags = [
  { property: 'product:availability', content: 'in stock' },
  { property: 'product:price:currency', content: 'AUD' },
]

const Product: NextPage = () => {
  const router = useRouter()
  const { trackEvent } = useGoogleAnalytics()

  const selectedStore = useStore(s => s.store.selectedStore)
  const location = useStore(store => store.location)

  const slug = router.query.slug as string

  let { data: product, isFetching } = useProductQuery<CoreProductEntity>(
    {
      product: slug,
      store: selectedStore?.slug,
      region: selectedStore?.slug ? undefined : location.region,
    },
    { cacheTime: 300000 },
  )

  useEffect(() => {
    if (!product) return

    track('Viewed Product', {
      id: product.id,
      name: product.productGroup?.name,
      brand: product.productGroup?.brand?.name,
      category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
      variant: product.factorDescription,
    })

    trackEvent('ec', 'setAction', 'detail')

    /* Send to Google Analytics */
    eventUA('view_item', {
      event_category: 'ecommerce',
      event_label: 'View product',
      non_interaction: true,
      items: [
        {
          id: product.id,
          name: product.productGroup?.name,
          brand: product.productGroup?.brand?.name,
          content_category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
          price: (product.specialPricing?.price ?? (product.pricing?.price || 0)) / 100,
          variant: product.factorDescription,
        },
      ],
    })

    eventGA4('view_item', {
      currency: 'AUD',
      value: (product.specialPricing?.price ?? (product.pricing?.price || 0)) / 100,
      items: [
        {
          item_id: product.id,
          item_name: product.productGroup?.name,
          item_brand: product.productGroup?.brand?.name,
          item_category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
          price: (product.specialPricing?.price ?? (product.pricing?.price || 0)) / 100,
          item_variant: product.factorDescription,
        },
      ],
    })

    /* Send to Facebook Pixel */
    if (typeof fbq !== 'undefined') {
      fbq?.('track', 'ViewContent', {
        content_ids: [product.id],
        content_category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
        content_name: `${product.productGroup?.brand?.name} ${product.productGroup?.name}`,
        content_type: 'product',
      })
    }
  }, [product])

  const isBundle = product?.type === ProductType.BUNDLE
  console.log(product)
  // console.log("isBundle",isBundle)

  const _title = isBundle
    ? product?.bundle?.title
    : `${product?.productGroup.brand?.name} ${product?.productGroup.name}`

  const imagesToPreload = useMemo(() => {
    let imagesArray = []

    product?.productGroup?.products?.forEach(curr => {
      const images = curr?.media?.forEach(i =>
        USED_IMAGE_SIZES.forEach(height => imagesArray.push(imageProxy(i.filename, { height }))),
      )
      imagesArray = imagesArray.concat(images)
    })

    return imagesArray
  }, [product])

  return (
    <LayoutMain
      title={_title}
      noContainer
      ogImage={product?.media?.[0] && imageProxy(product.media[0].filename, { height: 1200 })}
      images={imagesToPreload}
      description={isBundle ? product?.bundle?.description : product?.description}
      tags={tags}
    >
      {isBundle ? (
        <ProductBundleOverview product={product} isFetching={!product && isFetching} />
      ) : (
        <ProductOverview product={product} isFetching={!product && isFetching} />
      )}

      <Container size="lg" py={[12, , 12]}>
        <Delay wait={300}>
          <SpecialsSection
            slug={product?.productGroup?.categories?.[0]?.slug || 'beer'}
            title="More products"
          />
        </Delay>
      </Container>

      <Delay wait={500}>
        <Advert position="PRODUCT" />
      </Delay>

      <Delay wait={500}>
        <ProductReviews product={product} isFetching={!product && isFetching} />
      </Delay>
    </LayoutMain>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const queryCache = new QueryCache()

  const ip = requestIp.getClientIp(context.req)
  const result = geoip.lookup(ip)
  let region = result?.region || 'VIC' // Default to VIC

  const validRegions = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

  if (!validRegions.includes(region)) {
    region = 'VIC'
  }

  const { selected_store } = parseCookies(context)

  const { slug } = context.params
  const key: [string, any] = [
    `products/${slug}`,
    {
      params: { store: selected_store, region: selected_store ? undefined : region },
    },
  ]

  try {
    await queryCache.prefetchQuery(key, backend, { cacheTime: 30000 })
  } catch (e) {}

  return {
    props: {
      dehydratedState: dehydrate(queryCache),
    },
  }
}

export default memo(Product)
